import { useCallback, useEffect, useMemo, useState } from "react";
import { Task } from "@/types/v2";
import { useStore } from "@/stores/useStore";
import { useTaskPermissions } from "@/hooks/useTaskPermissions";
import { useTaskListActions } from "@/hooks/useTaskListActions";
import { filterTasks, TaskListFilters } from "@/lib/taskSelectors";
import { taskToRowProps } from "@/lib/taskRowProps";
import { getCardSection } from "@/lib/cards";
import { TaskRow } from "@/components/Tasks/TaskRow";
import { TaskFilterBar } from "@/components/Tasks/TaskFilterBar";
import { TaskFormDialog } from "@/components/Tasks/TaskFormDialog";
import { TaskListDialogs } from "@/components/Tasks/TaskListDialogs";
import { Button } from "@/components/Base/Button";
import { Plus } from "lucide-react";
import {
  usePersistedTaskFilters,
  EMPTY_FILTERS,
} from "@/hooks/usePersistedTaskFilters";
import { useRoPanelParams } from "@/hooks/useRoPanelParams";
import { useRoPanelAnalytics } from "@/hooks/useRoPanelAnalytics";

const PAGE_SIZE = 20;

export const TasksTab = () => {
  const { tasks, employees, cards, shop, shopId, user } = useStore();
  const taskListFilterPreset = useStore((s) => s.taskListFilterPreset);
  const setTaskListFilterPreset = useStore((s) => s.setTaskListFilterPreset);
  const permissions = useTaskPermissions();
  const actions = useTaskListActions();
  const { selectCard } = useRoPanelParams();
  const { logPanelOpened } = useRoPanelAnalytics();

  const { filters, setFilters, applyPresetFilters } = usePersistedTaskFilters(
    shopId,
    user?.id
  );
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // `tasks` arrives pre-sorted from the store (prepareTasks) — never re-sort.
  const filtered = useMemo(
    () => filterTasks(tasks, filters, undefined, actions.sessionCompletedIds),
    [tasks, filters, actions.sessionCompletedIds]
  );
  const visible = filtered.slice(0, visibleCount);
  const remaining = filtered.length - visibleCount;

  // task.relatedRO stores the card's Firestore id, not a human-readable
  // number — resolve it to the shop's configured RO-number field for display.
  const roNumberByCardId = useMemo(() => {
    if (!shop) return {} as Record<string, string>;
    return Object.fromEntries(
      cards.map((card) => [card.id, getCardSection(card, shop, "primaryField")])
    );
  }, [cards, shop]);

  // Clicking the RO number swaps this panel over to RO Details for that card,
  // which also paints the board's selection ring on it.
  const handleRoClick = useCallback(
    (task: Task) => {
      if (!task.relatedRO) return;
      selectCard(task.relatedRO);
      logPanelOpened();
    },
    [selectCard, logPanelOpened]
  );

  const handleFiltersChange = (next: TaskListFilters) => {
    setFilters(next);
    setVisibleCount(PAGE_SIZE);
    actions.resetSession();
  };

  // A toast (or other surface) can request the tab open pre-filtered by setting
  // a one-shot preset on the store. Apply it to live state only — writing it
  // through would make one toast click the user's saved filters forever — then
  // clear it so it doesn't fight subsequent manual filter changes.
  useEffect(() => {
    if (!taskListFilterPreset) return;
    applyPresetFilters(taskListFilterPreset);
    setVisibleCount(PAGE_SIZE);
    actions.resetSession();
    setTaskListFilterPreset(null);
  }, [taskListFilterPreset]); // eslint-disable-line react-hooks/exhaustive-deps

  const newTaskButton = (
    <Button className="size-8 p-0" aria-label="New task" title="New task">
      <Plus className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center justify-between gap-3 px-4 pb-2 pt-3">
        <h2 className="text-base font-bold leading-none text-foreground">
          Tasks ({filtered.length})
        </h2>
        <TaskFormDialog trigger={newTaskButton} />
      </div>
      <TaskFilterBar filters={filters} onChange={handleFiltersChange} />
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-4 pb-6">
        {tasks.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No tasks yet. Create the first one.
            </p>
            <TaskFormDialog trigger={newTaskButton} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No tasks match the current filters.
            </p>
            <Button
              variant="link"
              size="sm"
              onClick={() => handleFiltersChange(EMPTY_FILTERS)}
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <>
            {visible.map((task: Task) => (
              <TaskRow
                key={task.id}
                task={task}
                {...taskToRowProps(
                  task,
                  employees ?? [],
                  permissions,
                  task.relatedRO ? roNumberByCardId[task.relatedRO] : undefined
                )}
                {...actions.rowHandlers}
                onRoClick={task.relatedRO ? handleRoClick : undefined}
              />
            ))}
            {remaining > 0 && (
              <Button
                variant="ghost"
                className="w-full shrink-0"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                Show {Math.min(PAGE_SIZE, remaining)} more ({remaining}{" "}
                remaining)
              </Button>
            )}
          </>
        )}
      </div>
      <TaskListDialogs
        editingTask={actions.editingTask}
        setEditingTask={actions.setEditingTask}
        taskToDelete={actions.taskToDelete}
        setTaskToDelete={actions.setTaskToDelete}
        confirmDelete={actions.confirmDelete}
      />
    </div>
  );
};
